import CommonModal from 'components/common/CommonModal';
import DefaultButton from 'components/common/DefaultButton';
import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import {
  checkResignation,
  getTransferCandidates,
  resign,
  IResignationCheckResponse,
  ITransferCandidatesResponse,
  ITransferCandidate,
  IResignResponse,
} from 'function/api';
import React, {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {userInfoState} from 'utils/Atoms';
import {minmax} from 'utils/minmax';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';

type Props = {
  selectItem: {
    userUUID: string;
    contractorUUID: string;
    affiliation: string;
    role: string;
  };
  userName: string;
  closeModal: () => void;
  onSuccess?: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

/**
 * 퇴사 처리 모달 컴포넌트
 * OWNER 현황 확인 및 이양 대상 선택 후 퇴사 처리를 수행합니다.
 */
const ResignationModal = ({
  selectItem,
  userName,
  closeModal,
  onSuccess,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);

  // 상태
  const [step, setStep] = useState<'loading' | 'check' | 'transfer' | 'confirm' | 'complete' | 'error'>('loading');
  const [resignationCheck, setResignationCheck] = useState<IResignationCheckResponse | null>(null);
  const [transferCandidates, setTransferCandidates] = useState<ITransferCandidatesResponse | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<ITransferCandidate | null>(null);
  const [resignResult, setResignResult] = useState<IResignResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // 초기 로드 - 퇴사 가능 여부 확인
  useEffect(() => {
    const loadResignationCheck = async () => {
      const result = await checkResignation({
        userInfo,
        setIsLoading,
        setErrorModal,
        userUUID: selectItem.userUUID,
        contractorUUID: selectItem.contractorUUID,
      });

      if (result) {
        setResignationCheck(result);
        if (result.requiresTransfer) {
          // OWNER가 있으면 이양 대상 목록 조회
          const candidatesResult = await getTransferCandidates({
            userInfo,
            setIsLoading,
            setErrorModal,
            userUUID: selectItem.userUUID,
            contractorUUID: selectItem.contractorUUID,
            isContractorOwner: result.ownerInfo.isContractorOwner,
            source: 'backoffice',
          });

          if (candidatesResult) {
            setTransferCandidates(candidatesResult);
            if (candidatesResult.fallbackLevel === 'NONE') {
              setStep('error');
              setErrorMessage('이양 가능한 대상이 없어 퇴사 처리를 진행할 수 없습니다.');
            } else {
              setStep('transfer');
            }
          } else {
            setStep('error');
            setErrorMessage('이양 대상 목록을 불러오는데 실패했습니다.');
          }
        } else {
          setStep('check');
        }
      } else {
        setStep('error');
        setErrorMessage('퇴사 가능 여부를 확인하는데 실패했습니다.');
      }
    };

    loadResignationCheck();
  }, []);

  // 퇴사 처리 실행
  const handleResign = async () => {
    const result = await resign({
      userInfo,
      setIsLoading,
      setErrorModal,
      userUUID: selectItem.userUUID,
      contractorUUID: selectItem.contractorUUID,
      newOwnerUserUUID: selectedCandidate?.userUUID,
      isContractorOwner: resignationCheck?.ownerInfo.isContractorOwner || false,
      source: 'backoffice',
    });

    if (result) {
      setResignResult(result);
      setStep('complete');
    } else {
      setStep('error');
      setErrorMessage('퇴사 처리 중 오류가 발생했습니다.');
    }
  };

  // 모달 내용 렌더링
  const renderContent = () => {
    switch (step) {
      case 'loading':
        return (
          <ContentContainer>
            <DefaultText
              text="퇴사 가능 여부를 확인하는 중..."
              textStyle={{fontSize: minmax(24), textAlign: 'center'}}
            />
          </ContentContainer>
        );

      case 'check':
        // OWNER가 아닌 경우 - 바로 퇴사 확인
        return (
          <ContentContainer>
            <DefaultText
              text={`"${userName}"님을 "${selectItem.affiliation}"에서 퇴사 처리하시겠습니까?`}
              textStyle={{fontSize: minmax(24), marginBottom: minmax(20)}}
            />
            <InfoBox>
              <InfoItem>
                <InfoLabel>시공사 권한 삭제</InfoLabel>
                <InfoValue>{selectItem.role}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>관련 현장 권한</InfoLabel>
                <InfoValue>자동 삭제</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>출근 기록</InfoLabel>
                <InfoValue>보존됨</InfoValue>
              </InfoItem>
            </InfoBox>
            <ButtonContainer>
              <DefaultButton
                text="취소"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.lightGrey}}
                textStyle={{fontSize: minmax(20), color: colors.black}}
                onClick={closeModal}
              />
              <DefaultButton
                text="퇴사 처리"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.primary2}}
                textStyle={{fontSize: minmax(20)}}
                onClick={handleResign}
              />
            </ButtonContainer>
          </ContentContainer>
        );

      case 'transfer': {
        // OWNER인 경우 - 이양 대상 선택
        const ownerInfo = resignationCheck?.ownerInfo;
        return (
          <ContentContainer>
            <WarningBox>
              <DefaultText
                text={`"${userName}"님은 다음 항목의 OWNER입니다:`}
                textStyle={{fontSize: minmax(22), fontWeight: 'bold', marginBottom: minmax(10)}}
              />
              {ownerInfo?.isContractorOwner && (
                <OwnerItem>시공사: {selectItem.affiliation}</OwnerItem>
              )}
              {ownerInfo && ownerInfo.partnerCompaniesAsOwner.length > 0 && (
                <OwnerItem>
                  협력사: {ownerInfo.partnerCompaniesAsOwner.map(p => p.name).join(', ')} ({ownerInfo.partnerCompaniesAsOwner.length}개)
                </OwnerItem>
              )}
              {ownerInfo && ownerInfo.teamsAsOwner.length > 0 && (
                <OwnerItem>
                  팀: {ownerInfo.teamsAsOwner.map(t => t.name).join(', ')} ({ownerInfo.teamsAsOwner.length}개)
                </OwnerItem>
              )}
              {ownerInfo && ownerInfo.tongPlacesAsOwner.length > 0 && (
                <OwnerItem>
                  현장: {ownerInfo.tongPlacesAsOwner.map(t => t.name).join(', ')} ({ownerInfo.tongPlacesAsOwner.length}개)
                </OwnerItem>
              )}
            </WarningBox>

            <DefaultText
              text="새 OWNER 선택 (한 명에게 모든 권한 이양):"
              textStyle={{fontSize: minmax(20), marginTop: minmax(20), marginBottom: minmax(10)}}
            />

            {transferCandidates?.message && (
              <FallbackMessage>{transferCandidates.message}</FallbackMessage>
            )}

            <CandidateList>
              {transferCandidates?.candidates.map((candidate) => {
                const isDisabled = ownerInfo?.isContractorOwner && candidate.isOtherContractorOwner;
                return (
                  <CandidateItem
                    key={candidate.userUUID}
                    $selected={selectedCandidate?.userUUID === candidate.userUUID}
                    $disabled={isDisabled}
                    onClick={() => !isDisabled && setSelectedCandidate(candidate)}
                  >
                    <RadioButton $selected={selectedCandidate?.userUUID === candidate.userUUID} />
                    <CandidateInfo>
                      <CandidateName>{candidate.name}</CandidateName>
                      <CandidatePhone>{formatPhoneNumber({phone: candidate.phone})}</CandidatePhone>
                      <CandidateRole>{candidate.role}</CandidateRole>
                    </CandidateInfo>
                    {isDisabled && (
                      <DisabledBadge>다른 시공사 OWNER</DisabledBadge>
                    )}
                  </CandidateItem>
                );
              })}
            </CandidateList>

            {ownerInfo?.isContractorOwner && (
              <WarningText>
                * 시공사 OWNER는 CO_OWNER에게만 이양 가능합니다.
                <br />
                * 다른 시공사 OWNER인 사용자는 선택할 수 없습니다.
              </WarningText>
            )}

            <ButtonContainer>
              <DefaultButton
                text="취소"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.lightGrey}}
                textStyle={{fontSize: minmax(20), color: colors.black}}
                onClick={closeModal}
              />
              <DefaultButton
                text="OWNER 이양 및 퇴사처리"
                actived={selectedCandidate ? 1 : 0}
                buttonStyle={{
                  flex: 2,
                  backgroundColor: selectedCandidate ? colors.primary2 : colors.lighterGray,
                }}
                textStyle={{fontSize: minmax(20)}}
                onClick={() => selectedCandidate && setStep('confirm')}
              />
            </ButtonContainer>
          </ContentContainer>
        );
      }

      case 'confirm':
        // 최종 확인
        return (
          <ContentContainer>
            <DefaultText
              text="퇴사 처리를 실행하시겠습니까?"
              textStyle={{fontSize: minmax(26), fontWeight: 'bold', marginBottom: minmax(20)}}
            />
            <InfoBox>
              {resignationCheck?.ownerInfo.isContractorOwner && (
                <InfoItem>
                  <InfoLabel>시공사 OWNER 이양</InfoLabel>
                  <InfoValue>{userName} → {selectedCandidate?.name}</InfoValue>
                </InfoItem>
              )}
              {resignationCheck?.ownerInfo.partnerCompaniesAsOwner.length ? (
                <InfoItem>
                  <InfoLabel>협력사 OWNER 이양</InfoLabel>
                  <InfoValue>{resignationCheck.ownerInfo.partnerCompaniesAsOwner.length}개 → {selectedCandidate?.name}</InfoValue>
                </InfoItem>
              ) : null}
              {resignationCheck?.ownerInfo.teamsAsOwner.length ? (
                <InfoItem>
                  <InfoLabel>팀 OWNER 이양</InfoLabel>
                  <InfoValue>{resignationCheck.ownerInfo.teamsAsOwner.length}개 → {selectedCandidate?.name}</InfoValue>
                </InfoItem>
              ) : null}
              {resignationCheck?.ownerInfo.tongPlacesAsOwner.length ? (
                <InfoItem>
                  <InfoLabel>현장 OWNER 이양</InfoLabel>
                  <InfoValue>{resignationCheck.ownerInfo.tongPlacesAsOwner.length}개 → {selectedCandidate?.name}</InfoValue>
                </InfoItem>
              ) : null}
              <InfoItem>
                <InfoLabel>기존 OWNER 권한</InfoLabel>
                <InfoValue>전체 삭제</InfoValue>
              </InfoItem>
            </InfoBox>
            <ButtonContainer>
              <DefaultButton
                text="이전"
                actived={1}
                buttonStyle={{flex: 1, backgroundColor: colors.lightGrey}}
                textStyle={{fontSize: minmax(20), color: colors.black}}
                onClick={() => setStep('transfer')}
              />
              <DefaultButton
                text="퇴사 처리 실행"
                actived={1}
                buttonStyle={{flex: 2, backgroundColor: colors.vividRed}}
                textStyle={{fontSize: minmax(20)}}
                onClick={handleResign}
              />
            </ButtonContainer>
          </ContentContainer>
        );

      case 'complete':
        // 완료
        return (
          <ContentContainer>
            <SuccessIcon>✓</SuccessIcon>
            <DefaultText
              text="퇴사 처리가 완료되었습니다."
              textStyle={{fontSize: minmax(26), fontWeight: 'bold', marginBottom: minmax(20)}}
            />
            <InfoBox>
              <InfoItem>
                <InfoLabel>삭제된 시공사 권한</InfoLabel>
                <InfoValue>{resignResult?.deletedPermissions.contractor}개</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>삭제된 협력사 권한</InfoLabel>
                <InfoValue>{resignResult?.deletedPermissions.partnerCompany}개</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>삭제된 팀 권한</InfoLabel>
                <InfoValue>{resignResult?.deletedPermissions.team}개</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>삭제된 현장 권한</InfoLabel>
                <InfoValue>{resignResult?.deletedPermissions.tongPlace}개</InfoValue>
              </InfoItem>
              {resignResult?.transferredOwnership.contractor && (
                <InfoItem>
                  <InfoLabel>시공사 OWNER 이양</InfoLabel>
                  <InfoValue>완료</InfoValue>
                </InfoItem>
              )}
              <InfoItem>
                <InfoLabel>강제 퇴근 처리</InfoLabel>
                <InfoValue>
                  {(resignResult?.forcedClockOut.contractor || 0) +
                    (resignResult?.forcedClockOut.partnerCompany || 0) +
                    (resignResult?.forcedClockOut.tongPlace || 0)}건
                </InfoValue>
              </InfoItem>
            </InfoBox>
            <DefaultButton
              text="확인"
              actived={1}
              buttonStyle={{width: '100%', backgroundColor: colors.primary2, marginTop: minmax(20)}}
              textStyle={{fontSize: minmax(20)}}
              onClick={() => {
                if (onSuccess) {
                  onSuccess();
                } else {
                  closeModal();
                  window.location.reload();
                }
              }}
            />
          </ContentContainer>
        );

      case 'error':
        return (
          <ContentContainer>
            <ErrorIcon>!</ErrorIcon>
            <DefaultText
              text={errorMessage}
              textStyle={{fontSize: minmax(22), textAlign: 'center', color: colors.vividRed}}
            />
            <DefaultButton
              text="닫기"
              actived={1}
              buttonStyle={{width: '100%', backgroundColor: colors.lightGrey, marginTop: minmax(20)}}
              textStyle={{fontSize: minmax(20), color: colors.black}}
              onClick={closeModal}
            />
          </ContentContainer>
        );

      default:
        return null;
    }
  };

  return (
    <CommonModal closeModal={closeModal}>
      <ModalContainer>
        <ModalHeader>
          <DefaultText
            text={step === 'complete' ? '퇴사 처리 완료' : 'OWNER 이양 및 퇴사 처리'}
            textStyle={{fontSize: minmax(32), fontWeight: 'bold'}}
          />
          <DefaultImageView
            source="images/common/close.png"
            onClick={closeModal}
            style={{
              width: minmax(30),
              height: minmax(30),
              cursor: 'pointer',
            }}
          />
        </ModalHeader>
        {renderContent()}
      </ModalContainer>
    </CommonModal>
  );
};

export default ResignationModal;

// Styled Components
const ModalContainer = styled.div`
  width: 100%;
  max-width: ${minmax(600)};
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${minmax(20)};
  padding-bottom: ${minmax(15)};
  border-bottom: 1px solid ${colors.borderGray};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const WarningBox = styled.div`
  background-color: ${colors.lighterGray};
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(8)};
  padding: ${minmax(15)};
  margin-bottom: ${minmax(10)};
`;

const OwnerItem = styled.div`
  font-size: ${minmax(18)};
  color: ${colors.darkGray};
  padding: ${minmax(5)} 0;
  padding-left: ${minmax(10)};
`;

const InfoBox = styled.div`
  background-color: ${colors.lighterGray};
  border-radius: ${minmax(8)};
  padding: ${minmax(15)};
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${minmax(8)} 0;
  border-bottom: 1px solid ${colors.borderGray};
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: ${minmax(18)};
  color: ${colors.darkGray};
`;

const InfoValue = styled.span`
  font-size: ${minmax(18)};
  color: ${colors.black};
  font-weight: 500;
`;

const FallbackMessage = styled.div`
  font-size: ${minmax(16)};
  color: ${'#ffc107'};
  padding: ${minmax(10)};
  background-color: #fff3cd;
  border-radius: ${minmax(4)};
  margin-bottom: ${minmax(10)};
`;

const CandidateList = styled.div`
  max-height: ${minmax(250)};
  overflow-y: auto;
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(8)};
`;

const CandidateItem = styled.div<{$selected: boolean; $disabled?: boolean}>`
  display: flex;
  align-items: center;
  padding: ${minmax(12)} ${minmax(15)};
  border-bottom: 1px solid ${colors.borderGray};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  background-color: ${props => {
    if (props.$disabled) return colors.lighterGray;
    if (props.$selected) return '#e8f0f5';
    return 'white';
  }};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: ${props => {
      if (props.$disabled) return colors.lighterGray;
      if (props.$selected) return '#e8f0f5';
      return colors.lighterGray;
    }};
  }
`;

const RadioButton = styled.div<{$selected: boolean}>`
  width: ${minmax(20)};
  height: ${minmax(20)};
  border: 2px solid ${props => props.$selected ? colors.primary : colors.borderGray};
  border-radius: 50%;
  margin-right: ${minmax(12)};
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    width: ${minmax(10)};
    height: ${minmax(10)};
    background-color: ${props => props.$selected ? colors.primary : 'transparent'};
    border-radius: 50%;
  }
`;

const CandidateInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${minmax(15)};
`;

const CandidateName = styled.span`
  font-size: ${minmax(18)};
  font-weight: 500;
`;

const CandidatePhone = styled.span`
  font-size: ${minmax(16)};
  color: ${colors.darkGray};
`;

const CandidateRole = styled.span`
  font-size: ${minmax(14)};
  color: ${colors.primary};
  background-color: ${'#e8f0f5'};
  padding: ${minmax(2)} ${minmax(8)};
  border-radius: ${minmax(4)};
`;

const DisabledBadge = styled.span`
  font-size: ${minmax(12)};
  color: ${colors.vividRed};
  background-color: #ffebee;
  padding: ${minmax(2)} ${minmax(8)};
  border-radius: ${minmax(4)};
`;

const WarningText = styled.p`
  font-size: ${minmax(14)};
  color: ${colors.darkGray};
  margin-top: ${minmax(10)};
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${minmax(10)};
  margin-top: ${minmax(20)};
`;

const SuccessIcon = styled.div`
  width: ${minmax(60)};
  height: ${minmax(60)};
  background-color: ${colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${minmax(30)};
  margin: 0 auto ${minmax(20)};
`;

const ErrorIcon = styled.div`
  width: ${minmax(60)};
  height: ${minmax(60)};
  background-color: ${colors.vividRed};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${minmax(30)};
  font-weight: bold;
  margin: 0 auto ${minmax(20)};
`;
